import { useTranslation } from "@sagot/i18n";
import { exitSlotsOf, withExit } from "../exit-slots";
import type { Step } from "../types";
import type { FlowContext } from "./flow-context";
import { PathPicker } from "./path-select";
import { exitName } from "./step-summary";

const F = "engage.workflows.fields";

interface ExitsEditorProps<S extends Step> extends Pick<FlowContext, "paths" | "canAddPath"> {
  step: S;
  disabled: boolean;
  onChange: (step: S) => void;
}

/**
 * What happens next for each outcome of a step that waits for the visitor: a path, or on with the next step. The outcomes
 * are the step's exit slots, so the canvas handles and this list always show the same exits.
 */
export function ExitsEditor<S extends Step>({ step, onChange, paths, canAddPath, disabled }: ExitsEditorProps<S>) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="label">{t(`${F}.exits`)}</p>
      <div className="space-y-2 text-sm">
        {exitSlotsOf(step).map((slot) => {
          const label = exitName(slot, t);
          if (slot.fixed) {
            return (
              <div key={slot.key} className="flex items-center justify-between gap-2 rounded-md border border-line bg-surface-2 px-3 py-2" data-testid="fixed-exit">
                <span>{label}</span>
                <span className="text-xs font-semibold text-fg-muted">{t("engage.workflows.builder.path", { id: slot.target })}</span>
              </div>
            );
          }
          return (
            <div key={slot.key} className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2">
              <span>{label}</span>
              <PathPicker label={label} className="h-8 w-44 text-xs" value={slot.target} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.exitContinue`)} disabled={disabled} onChange={(path) => { onChange(withExit(step, slot.key, path)); }} />
            </div>
          );
        })}
      </div>
      <p className="hint">{t(`${F}.exitsHint`)}</p>
    </div>
  );
}
